// import { formatToCurrency } from "shared/utils";
import {
  ApplicationFeeStatus,
  AppliedDiscountInfo,
  ApplicantStatus
} from "shared/types";
// import { DiscountInfoTooltip } from "./DiscountInfoTooltip";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";

import { HugeiconsIcon } from "@hugeicons/react";
import {
  DiscountIcon,
  InformationCircleIcon,
  MinusSignIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { useAuthContext } from "providers/Auth";
import { formatToTwoDecimals } from "utils/format-to-two-decimals";
// import { formatDiscountAmountForTooltip } from "../utils/discountCalculations";

interface DiscountApplicant {
  id: number;
  application_fee_status: ApplicationFeeStatus | null;
  application_status: ApplicantStatus;
  discount_info?: AppliedDiscountInfo;
}

interface DiscountStatusProps {
  applicant: DiscountApplicant;
  onApplyDiscountClick: () => void;
  onCancelDiscountClick?: (applicantId: number) => void;
  isActiveDiscounts: boolean;
  alwaysAllowCustomDiscounts?: boolean;
}

export const DiscountStatus = ({
  applicant,
  onApplyDiscountClick,
  onCancelDiscountClick,
  isActiveDiscounts,
  alwaysAllowCustomDiscounts = false
}: DiscountStatusProps) => {
  const { permissions } = useAuthContext();
  const discountAmount =
    typeof applicant.discount_info?.discount_amount === "object"
      ? applicant.discount_info.discount_amount.cents
      : (applicant.discount_info?.discount_amount ?? 0);

  const isPaid = applicant.application_fee_status === ApplicationFeeStatus.Paid;
  const isRefunded =
    applicant.application_fee_status === ApplicationFeeStatus.Refunded;
  const isCanceled = applicant.application_status === ApplicantStatus.Canceled;
  const discountApplied = applicant.discount_info?.discount_applied;
  // Technically where we use this we are already checking alwaysDisplayForCustomDiscounts,
  // but we add a layer of safety here since this is the full logic of when this flag should be true
  const _alwaysAllowCustomDiscounts =
    permissions.canApplyCustomDiscounts && alwaysAllowCustomDiscounts;

  // 1. Discount applied and fee is paid - show "Applied" with tooltip
  if (discountAmount > 0 && isPaid && discountApplied)
    return (
      <div className="flex items-center gap-2">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={16}
          className="fill-content-100 text-white"
        />
        <span className="text-content-1000">Applied</span>
        <span className="text-content-300 -ml-1">
          (-${formatToTwoDecimals(discountAmount / 100)})
        </span>
      </div>
    );

  // 2. Discount applied and fee is unpaid - show "Applied" with tooltip and Cancel button
  if (discountAmount > 0 && !isPaid && discountApplied) {
    const appliedBy = applicant.discount_info?.applied_by;
    const showCancelButton =
      ((Boolean(appliedBy) && appliedBy !== "applicant") ||
        applicant.discount_info?.applied_by_user_name) &&
      !isRefunded &&
      !isCanceled;

    const canApplyAnyDiscounts =
      permissions.canApplyActiveDiscounts ||
      permissions.canApplyCustomDiscounts;

    return (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            size={16}
            className="fill-content-100 text-white"
          />
          <span className="text-content-1000">Applied</span>
          <span className="text-content-300 -ml-1">
            (-${formatToTwoDecimals(discountAmount / 100)})
          </span>

          <Show if={showCancelButton}>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.XS}
              disabled={!canApplyAnyDiscounts}
              tooltip={
                !canApplyAnyDiscounts
                  ? "You don't have permission to cancel discounts"
                  : undefined
              }
              tooltipZIndex={50}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancelDiscountClick?.(applicant.id);
              }}
            >
              Cancel
            </Button>
          </Show>
        </div>
      </div>
    );
  }

  // 3. Fee is paid/refunded/canceled or no active discounts - show minus icon
  if (
    isPaid ||
    isRefunded ||
    isCanceled ||
    (!isActiveDiscounts && !_alwaysAllowCustomDiscounts)
  )
    return (
      <HugeiconsIcon
        icon={MinusSignIcon}
        size={20}
        className="text-content-100"
      />
    );

  if (
    [
      ApplicantStatus.Started,
      ApplicantStatus.Pending,
      ApplicantStatus.Finished
    ].includes(applicant.application_status)
  )
    // 4. Default - show Apply button (but not for refunded applications)
    return (
      <>
        <Show
          if={
            !(
              permissions.canApplyActiveDiscounts ||
              permissions.canApplyCustomDiscounts
            )
          }
        >
          <HugeiconsIcon
            icon={MinusSignIcon}
            size={20}
            className="text-content-100"
          />
          <span className="text-content-200 mr-auto">Not applied</span>
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.XS}
            HugeIcon={DiscountIcon}
            className="flex items-center gap-2 text-content-300"
            disabled
            tooltip="You don't have permission to apply discounts manually. Please contact an administrator or share a discount code with the applicant"
            tooltipZIndex={50}
          >
            Apply discount
          </Button>
        </Show>
        <Show
          if={
            (Boolean(permissions.canApplyActiveDiscounts) ||
              Boolean(permissions.canApplyCustomDiscounts)) &&
            (isActiveDiscounts || _alwaysAllowCustomDiscounts)
          }
        >
          <HugeiconsIcon
            icon={MinusSignIcon}
            size={20}
            className="text-content-100"
          />
          <span className="text-content-200 mr-auto">Not applied</span>
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.XS}
            HugeIcon={DiscountIcon}
            className="flex items-center gap-2 text-primary-500 hover:text-primary-600"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onApplyDiscountClick();
            }}
          >
            Apply discount
          </Button>
        </Show>
      </>
    );

  // Cases where application is approved/rejected etc, where discount cannot be applied
  return (
    <HugeiconsIcon
      icon={MinusSignIcon}
      size={20}
      className="text-content-100"
    />
  );
};
